<template>
  <div class="job-action-column">
    <WdsMenu
      z-index="100"
      placement="bottom-end"
      manual-trigger
      :global="!isFullscreen"
      @menu:open="load"
    >
      <template #trigger="{ toggle }">
        <WdsButtonGhost
          :data-test="'contract-table-contract-row-actions-' + input.id"
          @click="toggle"
        >
          <WdsIcon name="overflow-menu-horizontal" :size="16" />
        </WdsButtonGhost>
      </template>

      <div v-if="column && !loading">
        <WdsMenuOption
          :href="
            laravelNamedRoute(namedRoutes.jobs.show, {
              job: column?.id ?? '',
            })
          "
        >
          <WdsStack gap-xs justify-center align-center>
            <WdsIcon name="portfolio" :size="16" />
            <span>{{ $t('View details') }}</span>
          </WdsStack>
        </WdsMenuOption>
        <template v-if="column.viewerCanEdit">
          <WdsMenuDivider />
          <WdsMenuTitle>
            {{ $t('Manage Job') }}
          </WdsMenuTitle>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.OVERVIEW,
              })
            "
          >
            {{ $t('Overview') }}
          </WdsMenuOption>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.DETAILS,
              })
            "
          >
            {{ $t('Details') }}
          </WdsMenuOption>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.BUDGET,
              })
            "
          >
            {{ $t('Budget') }}
          </WdsMenuOption>
          <WdsMenuOption
            :href="
              laravelNamedRoute(namedRoutes.yjob.edit, {
                yjob: column?.id ?? '',
                tab: JOB_TAB.SETTINGS,
              })
            "
          >
            {{ $t('Settings') }}
          </WdsMenuOption>
        </template>
        <template v-if="column.viewerCanCreateHire">
          <WdsMenuDivider />
          <WdsMenuOption @click="() => (showTalentModal = true)">
            <WdsStack gap-xs justify-center align-center>
              <WdsIcon name="hire" :size="16" />
              <span>{{ $t('Create hire offer') }}</span>
            </WdsStack>
          </WdsMenuOption>
        </template>
        <template v-if="column.viewerCanEdit">
          <WdsMenuOption @click="handleDuplicateJob">
            <WdsStack gap-xs justify-center align-center>
              <WdsIcon name="copy" :size="16" />
              <span>{{ $t('Duplicate job') }}</span>
            </WdsStack>
          </WdsMenuOption>
          <WdsMenuOption
            v-if="column.status != JobStatus.Removed && column.status != JobStatus.Completed"
            theme="DESTRUCTIVE"
            @click="handleEndJob"
          >
            <WdsStack gap-xs justify-center align-center>
              <WdsIcon name="trash-can" :size="16" />
              <span>{{ $t('End job') }}</span>
            </WdsStack>
          </WdsMenuOption>
        </template>
      </div>
      <div
        v-else
        class="tw-px-4 tw-py-2"
        :data-test="'contract-table-contract-row-actions-loading-' + input.id"
      >
        <WdsSpinner indeterminate />
      </div>
    </WdsMenu>
  </div>
  <teleport :to="tableGlobals">
    <BaseHtmlForm
      :id="`form-job-close-${column?.number}`"
      :action="`/job/${column?.number}/close`"
      method="PATCH"
      hidden
    />

    <WdsModal
      lg
      :show="showTalentModal"
      :title="$t('Direct Hire')"
      data-test="trusted-contact-modal"
      tabindex="0"
      @cancel="showTalentModal = false"
    >
      <template #header>
        <div>
          <WdsHeading>{{ $t('Direct Hire') }}</WdsHeading>
          <WdsText large class="tw-mt-2">
            {{ $t('Offer new job to specific worker') }}
          </WdsText>
        </div>
      </template>
      <TalentSelectionDirectHire
        v-model:trusted-contact="trustedContact"
        @cancel="showTalentModal = false"
        @confirm="showTalentModal = false"
      />
    </WdsModal>

    <DuplicateJobModal
      :show="showDuplicateModal"
      :job-id="input.id"
      :job-title="column?.name ?? ''"
      @cancel="showDuplicateModal = false"
      @success="handleDuplicateSuccess"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsMenuTitle,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, inject, ref, watch } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { FragmentType, graphql, useFragment } from '@/gql'
import { JobStatus } from '@/gql/graphql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { JOB_TAB } from '@/components/Job/constants'
import TalentSelectionDirectHire from '@/components/Job/Create/TalentSelectionDirectHire.vue'
import { TrustedContact } from '@/components/Job/hiring-types'
import { TableFullscreen, useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import DuplicateJobModal from '@/components/Job/DuplicateJobModal.vue'
import { useConfirmModal } from '@/composables/useConfirmModal'

const tableGlobals = useTableGlobalsSelector()

const JobActionDataRequest = graphql(`
  fragment JobActionDataRequest on Job {
    id
    number
    name
    viewerCanEdit
    status
    viewerCanDelete
    viewerCanCreateHire
  }
`)

const JobActionColumn = graphql(`
  fragment JobActionColumn on Job {
    id
    viewerCanEdit # For bulk actions
  }
`)

const props = defineProps<{ row: FragmentType<typeof JobActionColumn> }>()

const isFullscreen = inject(TableFullscreen, ref(false))

const input = computed(() => useFragment(JobActionColumn, props.row))

const { result, loading, load } = useLazyQuery(
  graphql(`
    query JobActionColumnMenu($id: ID!) {
      job(id: $id) {
        ...JobActionDataRequest
      }
    }
  `),
  () => ({ id: input.value.id }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const column = computed(() => useFragment(JobActionDataRequest, result.value?.job))
const showTalentModal = ref(false)
const trustedContact = ref<TrustedContact | undefined>(undefined)

// Duplicate job modal state
const showDuplicateModal = ref(false)

const handleDuplicateJob = () => {
  showDuplicateModal.value = true
}

const handleDuplicateSuccess = (newJobId: string) => {
  showDuplicateModal.value = false

  // Redirect to the new job's edit page
  window.location.href = laravelNamedRoute(namedRoutes.yjob.edit, {
    yjob: newJobId,
    tab: JOB_TAB.OVERVIEW,
  })
}

const { openConfirmModal } = useConfirmModal()

const handleEndJob = async () => {
  const confirmed = await openConfirmModal({
    actionName: $t('Complete job'),
    title: $t('Are you sure?'),
    description: $t(
      'Do you want to <strong>terminate this job</strong> and terminate any ongoing job process?',
    ),
    template: 'delete',
    confirmText: $t('Yes, mark the job as completed'),
    teleportTarget: '.graphql-table-globals-wrapper',
  })

  if (!confirmed) {
    return
  }

  const form = document.getElementById(`form-job-close-${column.value?.number}`)
  ;(form as HTMLFormElement | null)?.submit()
}

watch(
  () => trustedContact.value,
  () => {
    window.location.href = `${laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
      freelancer: trustedContact.value?.workerId,
    })}?job=${column.value?.id}`
  },
  { once: true },
)
</script>

<style scoped lang="scss">
.job-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
